import { Component, Input, OnInit } from '@angular/core';
import { ElementType } from 'src/app/_objects/element-type';
import { FromToDetail } from 'src/app/_objects/report-template';
import { ReportText } from 'src/app/_objects/report-text';
import { MousePoint, Notifier, ReportTemplateDesignService } from 'src/app/_services/report-template-design.service';
import { REPORT_TEMPLATE___APPLICATION_TYPE, StorageService } from '../../_services/storage.service';

@Component({
    selector: 'app-report-text-container',
    templateUrl: './report-text.component.html',
    styleUrls: ['./report-text.component.scss'],
    standalone: false
})
export class ReportTextComponent implements OnInit {

  @Input() public pageKey: string;
  @Input() public textKey: string;
  @Input() public elementLocation: string;
  @Input() public drawCustomElement: boolean;

  public reportText: ReportText;
  public isVisol: boolean;
  private notifier: Notifier;
  private resizeMarkerSize: number;
  private resizing: boolean;
  private oldValidValue: string;
  public isCustomDrawEnable: boolean;


  constructor(
    public designService: ReportTemplateDesignService,
    private storageService: StorageService,
  ) {
    this.resizeMarkerSize = 36;
    this.resizing = false;
  }

  public ngOnInit() {
    this.notifier = {
      componentName: 'report-text.component',
      ids: { pageId: this.pageKey, elementId: this.textKey }
    };
    this.isCustomDrawEnable = this.designService.customElementAdd;
    this.reportText = this.designService.getText(this.textKey);
    this.oldValidValue = this.reportText.text;
    const appType = this.storageService.fetchApplicationType(REPORT_TEMPLATE___APPLICATION_TYPE);
    if (appType) {
      this.isVisol = appType.IsVisol;
    }
    let elementNavigator = this.designService.navigatorElements
      .filter((x) => x.pageKey == this.pageKey)[0]
      .elementsDetails.filter((y) => y.location == this.elementLocation)[0]
      .elements;

    elementNavigator.filter(x => x.elementName == ElementType.Text).length > 0
      ? elementNavigator.filter(x => x.elementName == ElementType.Text)[0].elementData.push({
        id: this.textKey,
        page: this.pageKey,
        name: this.reportText.text,
      })
      : elementNavigator.push({
        elementName: ElementType.Text,
        isVisible: false,
        elementData: [
          {
            id: this.textKey,
            page: this.pageKey,
            name: this.reportText.text,
          },
        ],
      });
  }

  public getResizeMarkerSize(): string {
    return this.resizeMarkerSize + 'px';
  }

  public getResizeMarkerLeft(horizontal: string): string {
    switch (horizontal) {
      case 'left':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'center':
        return ((this.reportText.getWidthPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'right':
        return (this.reportText.getWidthPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public getResizeMarkerTop(vertical: string): string {
    switch (vertical) {
      case 'top':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'middle':
        return ((this.reportText.getHeightPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'bottom':
        return (this.reportText.getHeightPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public onClick(event: MouseEvent) {
    if (this.reportText.isSelectable) {
      this.designService.notifySelectElement(this.notifier, this.textKey, event.ctrlKey);
      event.stopPropagation();
    }
  }

  public onDragStart(event: DragEvent) {
    event.dataTransfer.setData('text', this.textKey);
    event.dataTransfer.effectAllowed = 'move';

    const fromDetail: FromToDetail = {
      location: this.reportText.getLocation(),
      pageId: this.pageKey
    };
    const dragPoint: MousePoint = {
      onElementX: event.offsetX,
      onElementY: event.offsetY
    };


    this.designService.notifyBeginDrag(this.notifier, ElementType.Text, fromDetail, dragPoint);
  }

  public onDragOver(event: DragEvent) {
    event.preventDefault();
    this.designService.highlightElement = true;
    event.dataTransfer.dropEffect = 'move';
    event.stopPropagation();
  }

  public onDrop(event: DragEvent) {
    event.preventDefault();
    this.designService.highlightElement = false;
    const template = { ...this.designService.originTemplate };
    template.reportTemplateContent = JSON.stringify(this.designService.getTemplate());
    this.designService.templateForUndo.push(template);
    const dragElemId = event.dataTransfer.getData('text');

    const toDetail: FromToDetail = {
      location: this.reportText.getLocation(),
      pageId: this.pageKey
    };
    const dropPoint: MousePoint = {
      onElementX: this.reportText.getLeftPx() + event.offsetX,
      onElementY: this.reportText.getTopPx() + event.offsetY
    };


    this.designService.notifyDrop(this.notifier, dragElemId, toDetail, dropPoint);
    event.dataTransfer.clearData('text');
    event.stopPropagation();
  }

  public onResizeMarkerDown(event: MouseEvent, vertical: string, horizontal: string) {
    event.preventDefault();
    this.resizing = true;
    const beginPoint: MousePoint = {
      onElementX: event.offsetX,
      onElementY: event.offsetY
    };
    this.designService.notifyBeginResize(this.notifier, ElementType.Text, horizontal, vertical, beginPoint);

    // this.resizeMarker = event.target as HTMLDivElement;
    // this.resizeMarker.addEventListener('mousemove', this.onResizing.bind(this), false);

    event.stopPropagation();
  }

  public onResizing(event: MouseEvent) {
    event.preventDefault();
    if (this.resizing) {
      // console.log('ReportText onResizing');
      this.designService.notifyResize(this.notifier, { onElementX: event.offsetX, onElementY: event.offsetY });
    }
    event.stopPropagation();
  }

  public onResizeMarkerUp(event: MouseEvent) {
    // console.log('ReportText onResizeMarkerUp');
    event.preventDefault();
    this.resizing = false;
    event.stopPropagation();
  }

  public onResizeMarkerLeave(event: MouseEvent) {
    // console.log('ReportText onResizeMarkerLeave');
    event.preventDefault();
    this.resizing = false;
    event.stopPropagation();
  }

  public enableEditMode(obj: any) {
    if (obj.baseDef.type !== 'static') { return; }
    obj.editMode = true;
    setTimeout(() => {
      if (document.getElementsByTagName('textarea').length > 0) {
        document.getElementsByTagName('textarea')[0].focus();
      }
    }, 100);
  }
  public isCustomElement(obj: any) {
    return obj.baseDef.isCustomElement ? true : false;
  }
  public disableEditMode(obj: any): void {
    if (!obj.text) { return; }
    obj.editMode = false;

    if (this.oldValidValue !== obj.text) {
      this.designService.notifyUpdateElementProperties(this.notifier, {
        id: obj.uuid,
        name: obj.metaText,
        value: obj.text,
        oldValue: this.oldValidValue
      });

      this.oldValidValue = obj.text;
    }
  }
}
